import React, { useState } from 'react';
import {
  Box,
  Text,
  VStack,
  Flex,
  Button,
  IconButton,
  Input,
  Checkbox,
  Tooltip,
  List,
  ListItem,
  useToast,
} from '@chakra-ui/react';
import {
  useDropzone,
  FileWithPath,
  FileRejection,
  FileError,
  Accept,
  ErrorCode,
} from 'react-dropzone';
import { FiUpload, FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

interface FileUploadItem {
  file: FileWithPath;
  comment: string;
  isFlagged: boolean;
}

interface FlagOption {
  label: string;
  isFileAllowed: (file: File) => boolean;
  disabledTooltip: string;
}

interface InputFileDragAndDropProps<TUploadResult = unknown> {
  maxFileSize: number;
  withComment?: boolean;
  flagOption?: FlagOption;
  accept?: Accept;
  customUploadInnerText?: string;
  uploadFile: (
    file: File,
    comment?: string,
    isFlagged?: boolean,
  ) => Promise<TUploadResult>;
  onUploadComplete?: (uploadedCount: number, results: TUploadResult[]) => void;
  onUploadError?: (error: unknown) => void;
}

export const InputFileDragAndDrop = <TUploadResult extends unknown = unknown>({
  maxFileSize,
  withComment = false,
  //flag per file
  flagOption,
  accept,
  customUploadInnerText,
  uploadFile,
  onUploadComplete,
  onUploadError,
}: InputFileDragAndDropProps<TUploadResult>): JSX.Element => {
  const { t } = useTranslation();
  const toast = useToast({
    position: 'bottom',
    duration: 5000,
    isClosable: true,
  });
  const [selectedFiles, setSelectedFiles] = useState<FileUploadItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = (acceptedFiles: FileWithPath[]) => {
    setSelectedFiles(prev => [
      ...prev,
      ...acceptedFiles.map(file => ({ file, comment: '', isFlagged: false })),
    ]);
  };

  const getFileErrorMessage = (error: FileError): string => {
    switch (error.code) {
      case ErrorCode.FileInvalidType:
        return t(
          'inputFileDragAndDrop.fileInvalidType',
          'Nieprawidłowy typ pliku. Dozwolone rozszerzenia: {{extensions}}',
          { extensions: Object.values(accept ?? {}).flat().join(', ') },
        );
      case ErrorCode.FileTooLarge:
        return t(
          'inputFileDragAndDrop.fileTooLarge',
          'Plik jest za duży. Maksymalny rozmiar pliku: {{maxSize}}',
          { maxSize: `${Math.round(maxFileSize / 1024 / 1024)}MB` },
        );
      case ErrorCode.FileTooSmall:
        return t('inputFileDragAndDrop.fileTooSmall', 'Plik jest zbyt mały');
      case ErrorCode.TooManyFiles:
        return t(
          'inputFileDragAndDrop.tooManyFiles',
          'Przekroczono dozwoloną liczbę plików',
        );
      default:
        return error.message;
    }
  };

  const onDropRejected = (fileRejections: FileRejection[]) => {
    fileRejections.forEach(fileRejection => {
      toast({
        title: t(
          'inputFileDragAndDrop.uploadError',
          'Błąd podczas dodawania pliku',
        ),
        description: (
          <List>
            {fileRejection.errors.map(error => (
              <ListItem key={error.code}>{getFileErrorMessage(error)}</ListItem>
            ))}
          </List>
        ),
        status: 'error',
      });
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    maxSize: maxFileSize,
    accept,
  });

  const removeFile = (indexToRemove: number) => {
    setSelectedFiles(files =>
      files.filter((_, index) => index !== indexToRemove),
    );
  };

  const updateFileComment = (index: number, comment: string) => {
    setSelectedFiles(files =>
      files.map((fileItem, i) =>
        i === index ? { ...fileItem, comment } : fileItem,
      ),
    );
  };

  const updateFileFlag = (index: number, isFlagged: boolean) => {
    setSelectedFiles(files =>
      files.map((fileItem, i) =>
        i === index ? { ...fileItem, isFlagged } : fileItem,
      ),
    );
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);

    try {
      const results = await Promise.all(
        selectedFiles.map(({ file, comment, isFlagged }) =>
          uploadFile(file, comment || undefined, isFlagged),
        ),
      );
      setSelectedFiles([]);
      onUploadComplete?.(selectedFiles.length, results);
    } catch (error) {
      onUploadError?.(error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <VStack spacing={4} align='stretch'>
      <Box
        {...getRootProps()}
        p={7}
        border='2px dashed'
        borderColor={isDragActive ? 'blue.400' : 'gray.300'}
        borderRadius='md'
        bg={isDragActive ? 'blue.50' : 'gray.50'}
        textAlign='center'
        cursor='pointer'
        transition='all 0.2s'
        _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
        _dark={{
          bg: '#1A202C',
          borderColor: 'white.100',
          color: 'white.100',
        }}
      >
        <input {...getInputProps()} />
        <VStack spacing={2}>
          <FiUpload size={24} color={isDragActive ? '#3182CE' : '#A0AEC0'} />
          <Text
            fontSize='sm'
            color={isDragActive ? 'blue.600' : 'gray.600'}
            _dark={{ color: isDragActive ? 'blue.600' : 'white.100' }}
          >
            {isDragActive
              ? t('inputFileDragAndDrop.dropFilesHere', 'Upuść pliki tutaj')
              : customUploadInnerText ||
                t(
                  'inputFileDragAndDrop.dragAndDropOrChoose',
                  'Przeciągnij i upuść plik lub',
                )}
          </Text>
          <Button
            size='sm'
            bg='blue.500'
            color='white'
            _hover={{ bg: 'blue.600' }}
          >
            {t('selectFile', 'Wybierz plik')}
          </Button>
          <Text fontSize='xs' color='gray.500'>
            {t(
              'inputFileDragAndDrop.maxFileSize',
              'Maksymalny rozmiar pliku: {{maxSize}}',
              { maxSize: `${Math.round(maxFileSize / 1024 / 1024)}MB` },
            )}
          </Text>
        </VStack>
      </Box>

      {selectedFiles.length > 0 && (
        <Box>
          <Text fontSize='sm' fontWeight='medium' mb={2}>
            {t(
              'inputFileDragAndDrop.choosenFilesWithNumber',
              'Wybrane pliki ({{length}}):',
              { length: selectedFiles.length },
            )}
          </Text>
          <VStack spacing={2} align='stretch' mb={3}>
            {selectedFiles.map((fileItem, index) => (
              <VStack
                key={index}
                p={3}
                bg='gray.100'
                borderRadius='md'
                borderWidth={1}
                spacing={2}
                align='stretch'
                _dark={{
                  bg: '#1A202C',
                  borderColor: 'white.100',
                  color: 'white.100',
                }}
              >
                <Flex justify='space-between' align='center'>
                  <Flex align='center' gap={2} fontSize='sm'>
                    <Text>{fileItem.file.name}</Text>
                    <Text color='gray.500' fontSize='xs'>
                      {(fileItem.file.size / 1024 / 1024).toFixed(2)} MB
                    </Text>
                  </Flex>
                  <IconButton
                    aria-label={t('delete', 'Usuń')}
                    icon={<FiX />}
                    size='sm'
                    variant='ghost'
                    onClick={() => removeFile(index)}
                  />
                </Flex>
                {withComment && (
                  <Input
                    placeholder={t(
                      'inputFileDragAndDrop.addCommentOptional',
                      'Dodaj komentarz (opcjonalne)',
                    )}
                    size='sm'
                    value={fileItem.comment}
                    onChange={e => updateFileComment(index, e.target.value)}
                  />
                )}
                {flagOption && (
                  <Tooltip
                    label={flagOption.disabledTooltip}
                    isDisabled={flagOption.isFileAllowed(fileItem.file)}
                    hasArrow
                  >
                    <Checkbox
                      size='sm'
                      isChecked={fileItem.isFlagged}
                      isDisabled={!flagOption.isFileAllowed(fileItem.file)}
                      onChange={e => updateFileFlag(index, e.target.checked)}
                      variant='ghost'
                    >
                      {flagOption.label}
                    </Checkbox>
                  </Tooltip>
                )}
              </VStack>
            ))}
          </VStack>
          <Button
            colorScheme='blue'
            size='sm'
            onClick={handleUpload}
            isLoading={isUploading}
            loadingText={t('inputFileDragAndDrop.sending', 'Wysyłanie...')}
          >
            {t('inputFileDragAndDrop.sendWithNumber', 'Wyślij ({{length}})', {
              length: selectedFiles.length,
            })}
          </Button>
        </Box>
      )}
    </VStack>
  );
};
